<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import MessageFunnel from './MessageFunnel.vue';
import WeekdayHourHeatmap from './WeekdayHourHeatmap.vue';

const { t } = useI18n();

const props = defineProps({
  report: { type: Object, default: null },
  loading: { type: Boolean, default: false },
});

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const rates = computed(() => [
  {
    key: 'delivery',
    label: t('FLOW_BUILDER.REPORTS.DELIVERY.RATE_DELIVERY'),
    value: percentFormatter.format(props.report?.rates?.delivery ?? 0),
    description: t('FLOW_BUILDER.REPORTS.DELIVERY.RATE_DELIVERY_DESC'),
  },
  {
    key: 'open',
    label: t('FLOW_BUILDER.REPORTS.DELIVERY.RATE_OPEN'),
    value: percentFormatter.format(props.report?.rates?.open ?? 0),
    description: t('FLOW_BUILDER.REPORTS.DELIVERY.RATE_OPEN_DESC'),
  },
]);
</script>

<template>
  <div class="flex flex-col gap-6">
    <div
      class="border border-n-slate-4 bg-n-solid-1 rounded-lg p-4 flex flex-col gap-3"
    >
      <span class="text-sm font-semibold text-n-slate-12">
        {{ t('FLOW_BUILDER.REPORTS.DELIVERY.FUNNEL_TITLE') }}
      </span>
      <div v-if="loading" class="flex flex-col gap-3">
        <div
          v-for="n in 4"
          :key="`funnel-${n}`"
          class="h-8 rounded-lg bg-n-alpha-2 animate-pulse"
        />
      </div>
      <MessageFunnel v-else :funnel="report?.messages ?? {}" />
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <template v-if="loading">
        <div
          v-for="n in 2"
          :key="`rate-skeleton-${n}`"
          class="flex flex-col gap-2 border rounded-lg border-n-slate-4 bg-n-solid-1 p-4 animate-pulse"
        >
          <div class="h-3 w-32 rounded bg-n-alpha-2" />
          <div class="h-7 w-24 rounded bg-n-alpha-2 mt-0.5" />
          <div class="h-3 w-40 rounded bg-n-alpha-2 mt-1" />
        </div>
      </template>
      <template v-else>
        <div
          v-for="rate in rates"
          :key="rate.key"
          class="flex flex-col gap-1 border rounded-lg border-n-slate-4 bg-n-solid-1 p-4"
        >
          <span
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ rate.label }}
          </span>
          <span class="text-2xl font-semibold text-n-slate-12 tabular-nums">
            {{ rate.value }}
          </span>
          <span class="text-xs text-n-slate-10">{{ rate.description }}</span>
        </div>
      </template>
    </div>

    <div
      class="border border-n-slate-4 bg-n-solid-1 rounded-lg p-4 flex flex-col gap-3"
    >
      <span class="text-sm font-semibold text-n-slate-12">
        {{ t('FLOW_BUILDER.REPORTS.DELIVERY.DISPATCHES_PER_DAY_HOUR') }}
      </span>
      <div v-if="loading" class="h-56 rounded-md bg-n-alpha-2 animate-pulse" />
      <WeekdayHourHeatmap v-else :grid="report?.weekday_hour_heatmap ?? []" />
    </div>
  </div>
</template>
